/*
 * IRO-54 — Inline expanding search overlay
 *
 * Opens from the header search icon, queries Shopify's Predictive Search
 * API (/search/suggest.json) with a 200ms debounce, and renders product +
 * collection results inline. Enter on a full query still navigates to
 * /search?q=... (the form submits naturally).
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true" on the overlay container
 *   - focus trap (Tab cycles inside the overlay while open)
 *   - ESC closes
 *   - Arrow keys navigate result list; Enter activates highlighted item
 *   - aria-live region announces result count to assistive tech
 *   - Restores focus to the search icon on close
 *   - prefers-reduced-motion suppresses transitions
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 200;
  const MIN_QUERY_LEN = 2;
  const PRODUCT_LIMIT = 6;

  class SearchOverlay {
    constructor(overlay, trigger) {
      this.overlay = overlay;
      this.trigger = trigger;
      this.backdrop = overlay.querySelector('[data-search-backdrop]');
      this.input = overlay.querySelector('[data-search-input]');
      this.closeBtn = overlay.querySelector('[data-search-close]');
      this.results = overlay.querySelector('[data-search-results]');
      this.status = overlay.querySelector('[data-search-status]');
      this.form = overlay.querySelector('[data-search-form]');

      this.debounceTimer = null;
      this.activeController = null;
      this.activeIndex = -1;
      this.options = [];
      this.isOpen = false;
      this.prevFocus = null;
      this.docKeydown = null;

      this.bind();
    }

    bind() {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
      this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop.addEventListener('click', () => this.close());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onInputKeydown(e));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.prevFocus = document.activeElement;

      // Position panel below the header — measured at open time so we
      // honor any sticky-header / announcement-bar changes.
      const header = document.querySelector('.site-header');
      const headerRect = header ? header.getBoundingClientRect() : null;
      const top = headerRect ? Math.max(0, headerRect.bottom) : 0;
      this.overlay.style.setProperty('--search-overlay-top', `${top}px`);

      // Anchor desktop panel under the search icon — measure the trigger's
      // right edge and translate to a margin-right from the viewport.
      // (Mobile breakpoint ignores this — full-screen takeover.)
      const triggerRect = this.trigger.getBoundingClientRect();
      const rightFromViewport = Math.max(0, window.innerWidth - triggerRect.right);
      this.overlay.style.setProperty('--search-overlay-right', `${rightFromViewport}px`);

      this.overlay.hidden = false;
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.docKeydown = (e) => {
        if (e.key === 'Escape') {
          this.close();
        } else if (e.key === 'Tab') {
          this.trapTab(e);
        }
      };
      document.addEventListener('keydown', this.docKeydown);

      // Focus on next frame so the transition (if any) is not jumped.
      requestAnimationFrame(() => this.input.focus());
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      if (this.activeController) {
        this.activeController.abort();
        this.activeController = null;
      }
      clearTimeout(this.debounceTimer);

      this.overlay.hidden = true;
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.input.value = '';
      this.results.innerHTML = '';
      this.options = [];
      this.activeIndex = -1;
      this.status.textContent = '';

      if (this.docKeydown) {
        document.removeEventListener('keydown', this.docKeydown);
        this.docKeydown = null;
      }

      if (this.prevFocus && typeof this.prevFocus.focus === 'function') {
        this.prevFocus.focus();
      } else {
        this.trigger.focus();
      }
    }

    onInput() {
      const q = this.input.value.trim();
      clearTimeout(this.debounceTimer);

      if (q.length < MIN_QUERY_LEN) {
        if (this.activeController) {
          this.activeController.abort();
          this.activeController = null;
        }
        this.results.innerHTML = '';
        this.options = [];
        this.activeIndex = -1;
        this.status.textContent = '';
        return;
      }

      this.debounceTimer = setTimeout(() => this.fetchResults(q), DEBOUNCE_MS);
    }

    async fetchResults(q) {
      if (this.activeController) this.activeController.abort();
      this.activeController = new AbortController();

      const url = new URL('/search/suggest.json', window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', PRODUCT_LIMIT);
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const res = await fetch(url.toString(), {
          signal: this.activeController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Predictive search HTTP ' + res.status);
        const json = await res.json();
        const products = json?.resources?.results?.products || [];
        const collections = json?.resources?.results?.collections || [];
        this.renderResults(q, products, collections);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[search] fetch failed:', err.message);
        this.status.textContent = '';
      } finally {
        if (this.activeController && this.activeController.signal.aborted === false) {
          this.activeController = null;
        }
      }
    }

    renderResults(q, products, collections) {
      this.results.innerHTML = '';
      this.options = [];

      const total = products.length + collections.length;
      if (total === 0) {
        const empty = document.createElement('div');
        empty.className = 'search-overlay__empty';
        empty.textContent = `No results for "${q}"`;
        this.results.appendChild(empty);
        this.status.textContent = `No results for ${q}`;
        return;
      }

      if (products.length) {
        this.results.appendChild(this.renderSection('Products', products, 'product'));
      }
      if (collections.length) {
        this.results.appendChild(this.renderSection('Collections', collections, 'collection'));
      }

      this.status.textContent = `${total} ${total === 1 ? 'result' : 'results'} for ${q}`;
      this.activeIndex = -1;
    }

    renderSection(label, items, kind) {
      const section = document.createElement('div');
      section.className = 'search-overlay__section';

      const heading = document.createElement('div');
      heading.className = 'search-overlay__section-label';
      heading.textContent = `${label} (${items.length})`;
      section.appendChild(heading);

      const list = document.createElement('ul');
      list.className =
        'search-overlay__list' +
        (kind === 'collection' ? ' search-overlay__list--collections' : '');
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', label);

      items.forEach((item) => {
        const li = kind === 'product' ? this.buildProductRow(item) : this.buildCollectionRow(item);
        list.appendChild(li);
      });

      section.appendChild(list);
      return section;
    }

    buildProductRow(product) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');

      const link = document.createElement('a');
      link.className = 'search-overlay__row';
      link.href = product.url;

      const img = document.createElement('img');
      img.className = 'search-overlay__row-img';
      img.loading = 'lazy';
      img.alt = '';
      if (product.image) img.src = product.image;
      link.appendChild(img);

      const textWrap = document.createElement('div');
      textWrap.className = 'search-overlay__row-text';
      const title = document.createElement('span');
      title.className = 'search-overlay__row-title';
      title.textContent = product.title;
      textWrap.appendChild(title);
      link.appendChild(textWrap);

      if (product.price) {
        const price = document.createElement('span');
        price.className = 'search-overlay__row-price';
        price.textContent = this.formatPrice(product.price);
        link.appendChild(price);
      }

      const arrow = document.createElement('span');
      arrow.className = 'search-overlay__row-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      link.appendChild(arrow);

      li.appendChild(link);
      this.options.push(link);
      return li;
    }

    buildCollectionRow(collection) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');

      const link = document.createElement('a');
      link.className = 'search-overlay__row search-overlay__row--collection';
      link.href = collection.url;
      link.textContent = collection.title;

      li.appendChild(link);
      this.options.push(link);
      return li;
    }

    onInputKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.moveActive(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.moveActive(-1);
      } else if (e.key === 'Enter') {
        if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
          e.preventDefault();
          this.options[this.activeIndex].click();
        }
        // Otherwise the form submits naturally to /search?q=...
      }
    }

    onSubmit(e) {
      const q = this.input.value.trim();
      if (!q) e.preventDefault();
    }

    moveActive(delta) {
      if (this.options.length === 0) return;
      this.activeIndex =
        (this.activeIndex + delta + this.options.length) % this.options.length;
      this.options.forEach((el, i) => {
        if (i === this.activeIndex) {
          el.classList.add('is-active');
          el.parentElement?.setAttribute('aria-selected', 'true');
          el.scrollIntoView({ block: 'nearest' });
        } else {
          el.classList.remove('is-active');
          el.parentElement?.removeAttribute('aria-selected');
        }
      });
    }

    trapTab(e) {
      const focusables = this.overlay.querySelectorAll(
        'input, button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusables).filter((el) => el.offsetParent !== null);
      if (visible.length === 0) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    formatPrice(raw) {
      // Predictive Search returns a money string already formatted (e.g.
      // "$129.95"). If we get a bare number, format as USD; if anything
      // else, pass through unchanged.
      if (raw == null) return '';
      if (typeof raw === 'string' && /[^\d.]/.test(raw)) return raw;
      const n = parseFloat(raw);
      if (isNaN(n)) return String(raw);
      // Shopify Predictive Search delivers cents on some shapes, decimals on others.
      // Heuristic: values >= 1000 are likely cents; otherwise treat as decimal dollars.
      const dollars = n >= 1000 ? n / 100 : n;
      return `$${dollars.toFixed(2)}`;
    }
  }

  function init() {
    const overlay = document.querySelector('[data-search-overlay]');
    const trigger = document.querySelector('[data-search-toggle]');
    if (!overlay || !trigger) return;
    new SearchOverlay(overlay, trigger);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
